import { FieldMaxLengths } from "@/app/account/(main)/shipping/AddressForm";
import { CreateNewPasswordError } from "@/declarations/assertions/isCreateNewPasswordError";
import { baseApi } from "@/store/baseApi";
import { mapUsersServiceError } from "@/store/usersServiceErrors";
import Cookies from "js-cookie";

/** Distribution-list type ID for "Order Update" CC emails (only dltId currently used by the UI). */
export const DLT_ID_ORDER_UPDATE = 1;

export interface ModifyNameRequest {
  prefix?: string;
  npId?: number | null;
  firstName: string;
  middleName?: string;
  lastName: string;
  suffix?: string;
}

export interface ModifyPasswordRequest {
  oldPassword: string;
  newPassword: string;
}

export interface ModifyEmailRequest {
  newEmail: string;
}

export interface CreateCCEmailRequest {
  email: string;
  dltId: number;
}

export interface ModifyCCEmailRequest {
  dleId: number;
  dltId: number;
  newEmail: string;
}

export interface DeleteCCEmailRequest {
  dleId: number;
  dltId: number;
}

export interface SendCCEmailInvitesRequest {
  emailString: string;
  dltId: number;
  culture?: string;
  verId?: number;
}

/**
 * Body for the admin-only `setProfessionalAndVerified` mutation. The users µ requires the
 * caller's JWT to carry an impersonator id — non-admin callers get 403.
 */
export interface SetProfessionalAndVerifiedRequest {
  verified: boolean;
  isProfessionalOffice: boolean;
}

const mapOrdersCreditCardToFrontend = (card: any): Account.Billing.CreditCard => ({
  CardId: card.cardId || "",
  Type: card.type || "",
  Name: card.nameOnCard || "",
  Last4Digits: card.last4Digits || "",
  IsPrimary: card.primary === true,
  IsExpired: card.isExpired,
  ExpMonth: card.expirationMonth,
  ExpYear: card.expirationYear,
  BillingAddress: card.billingAddress
    ? {
        Type: "",
        Name: card.billingAddress.name || "",
        City: card.billingAddress.city || "",
        RegionCD: card.billingAddress.region || "",
        PostalCD: card.billingAddress.postalCode || "",
        CountryCD: card.billingAddress.country || "",
        PhoneNumber: "",
        IsPrimary: false,
        IsCommercial: false,
        Streets: card.billingAddress.streets || [],
        IsValidated: null,
      }
    : {
        Type: "",
        Name: "",
        City: "",
        RegionCD: "",
        PostalCD: "",
        CountryCD: "",
        PhoneNumber: "",
        IsPrimary: false,
        IsCommercial: false,
        Streets: [],
        IsValidated: null,
      },
});

const newCreditCardToOrdersPayload = (card: Account.Billing.NewCreditCard) => ({
  sourceId: card.SourceId,
  nameOnCard: card.Name,
  type: card.Type,
  isDefaultCard: card.IsPrimary === true,
  billingAddress: {
    name: card.Name,
    city: card.BillingAddress.City,
    region: card.BillingAddress.RegionCD,
    postalCode: card.BillingAddress.PostalCD,
    country: card.BillingAddress.CountryCD,
    streets: card.BillingAddress.Streets,
  },
});

export const userApi = baseApi
  .enhanceEndpoints({
    addTagTypes: [
      "USER",
      "SHIPPING_ADDRESSES",
      "PRIMARY_SHIPPING_ADDRESS",
      "MY_ACCOUNT_INFO",
      "LAST_PURCHASED_PRODUCTS",
      "ACCOUNT_BUSINESS_TYPE",
      "POSTAL_CODE_DETAILS",
      "SHOPPING_CART",
      "CREDIT_CARDS",
      "USER_FULL_NAME",
      "MY_LICENSES",
      "TOTAL_ORDERS",
    ],
  })
  .injectEndpoints({
    endpoints: (builder) => ({
      getUser: builder.query<UserState, void>({
        // Calls the public /users/user-context endpoint, which always returns 200. The body
        // carries two orthogonal flags that map to two independent UserState flags:
        //
        //   backend `isAuthenticated`  → UserState.isAuthorized   (is the session signed in?)
        //   backend `guestOnlySw`      → UserState.IsGuestUser    (is this a guest-only account?)
        //
        // The "JWT expired but personId resolves to a real account" state therefore lands at
        // IsGuestUser=false + isAuthorized=false — which the call sites that gate account
        // actions (reorderClientPage, addToShoppingList, productDetailClientPage,
        // chooseShippingAddressModal, etc.) already handle correctly. Header reads only
        // IsGuestUser, so the name displays for this state.
        //
        // UserInfoResponse from the backend, so userName is null exactly when no user is
        // identifiable, regardless of whether ugrId would have been serialized.
        query: () => "next/users/user-context",
        providesTags: () => ["USER"],
        transformResponse: (response: UsersDetailsResponse): UserState => {
          // Pure-guest sentinel: the body comes back without identity fields populated.
          // We treat anyone without a userName as a fresh visitor.
          if (!response || !response.userName) {
            return { IsGuestUser: true, isAuthorized: false, userHasBeenRetrieved: true };
          }
          // A real (non-guest-only) account is identifiable; IsGuestUser is false even when
          // the session isn't authenticated (returning user, JWT expired, personId still valid).
          const isRealAccount = response.guestOnlySw === false;
          const isAuthorized = response.isAuthenticated === true;
          return {
            IsGuestUser: !isRealAccount,
            isAuthorized,
            userHasBeenRetrieved: true,
            IsAdmin: response.isAdmin === true,
            IsVendorAdmin: response.isVendorAdmin === true,
            UserName: response.userName ?? "",
            UserId: response.ugrId,
            FirstName: response.firstName ?? "",
            MiddleName: response.middleName ?? undefined,
            LastName: response.lastName ?? "",
            EmailAddress: response.emailAddress ?? "",
            Prefix: response.prefix ?? undefined,
            NickName: response.nickName ?? "",
            ActiveCd: response.activeCd ?? 1,
            UserIdHash: response.userIdHash ?? undefined,
            Impersonator: response.impersonator ?? undefined,
            IsProfessionalOffice: response.isProfessionalOffice ?? undefined,
            Profession:
              response.pfId != null
                ? {
                    id: response.pfId,
                    Name: response.professionName ?? "",
                    Other: response.professionOther ?? undefined,
                  }
                : undefined,
            VendorId: response.vendorId ?? undefined,
            LoginLink: response.loginLink ?? undefined,
            // `TotalOrders` is part of the legacy `UserState` shape but is NOT sourced from
            // /users/details. The real lifetime order count lives on the orders µ and is
            // fetched independently via `useGetTotalOrdersQuery` (see getTotalOrders below).
            // Consumers — currently only `useAnalyticsManager.ts` — call both hooks side by
            // side and pass the `totalOrders` value as a separate argument to analytics
            // helpers (e.g. `convertUserStateToUserDataForAnalytics(user, totalOrders)`); the
            // count is intentionally NOT merged back into this cached `UserState`. The `0`
            // here is just a placeholder so the legacy `UserState.TotalOrders` field stays
            // defined for typing and any direct reads (the field is otherwise unused now).
            TotalOrders: 0,
            Regions: [],
          } as unknown as UserState;
        },
      }),

      /**
       * Lifetime order count for the logged-in user — replaces what windfall used to embed
       * as WFVUser.TotalOrders. Lives on the orders µ; UI proxies via /rest/next/orders/total-orders.
       *
       * Follows the same caching pattern as every other user-info query in this codebase
       * (useGetUserQuery, useGetUserNameDetailsQuery, useGetAccountSummaryQuery): in-memory
       * RTK Query cache that survives SPA navigation but re-fetches on full page reload.
       * Logout still clears it via the existing {@code dispatch(resetApiState())} in
       * {@code accountMenuV2.tsx}. {@code providesTags: ["TOTAL_ORDERS"]} is kept so a future
       * checkout-completion mutation can {@code invalidatesTags} for forced refresh.
       */
      getTotalOrders: builder.query<number, void>({
        query: () => "next/orders/total-orders",
        providesTags: ["TOTAL_ORDERS"],
        transformResponse: (response: { totalOrders?: number } | null) => response?.totalOrders ?? 0,
      }),

      login: builder.mutation<
        UserLoginResponse,
        { username: string; password: string; agreeToTermsOfService: boolean | null }
      >({
        query: ({ username, password, agreeToTermsOfService }) => ({
          url: "next/login",
          method: "POST",
          body: JSON.stringify({
            username,
            password,
            agreeToTermsOfService,
          }),
        }),
      }),

      logout: builder.mutation<WindfallRestfulResponse.ResponsePacket, void>({
        query: () => ({
          url: "user/logout",
          method: "GET",
        }),
      }),
      // Migrated from neo /neo/user/getShippingAddressesForUser. The users service already
      // serializes the response in PascalCase matching Account.Addresses.Address, so no
      // transformResponse is needed.
      getAllShippingAddresses: builder.query<Account.Addresses.Address[], void>({
        query: () => "next/users/getShippingAddressesForUser",
        providesTags: ["SHIPPING_ADDRESSES"],
      }),
      getPrimaryShippingAddress: builder.query<Account.Addresses.Address, void>({
        query: () => "neo/user/getPrimaryShippingAddress",
        providesTags: ["PRIMARY_SHIPPING_ADDRESS"],
      }),
      getUserNameDetails: builder.query<Account.NameData | null, void>({
        query: () => "next/users/user-name-data",
        providesTags: ["USER_FULL_NAME"],
        transformResponse: (response: Account.NameData | null): Account.NameData | null => {
          if (response == null || typeof response !== "object") {
            return null;
          }
          return response;
        },
      }),
      getExistingCreditCards: builder.query<Account.Billing.CreditCard[], void>({
        query: () => "next/orders/user/credit-cards", // Goes through Next.js API route (rewritten from /rest/next/* to /rest/*)
        providesTags: ["CREDIT_CARDS"],
        transformResponse: (response: any): Account.Billing.CreditCard[] =>
          Array.isArray(response) ? response.map(mapOrdersCreditCardToFrontend) : [],
      }),

      setShippingAddressAsPrimary: builder.mutation<void, number>({
        query: (addressId) => ({
          url: "next/users/makeAddressPrimary",
          method: "POST",
          body: "id=" + addressId,
          headers: {
            Accept: "application/json",
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }),
        invalidatesTags: [
          "USER",
          "PRIMARY_SHIPPING_ADDRESS",
          "SHIPPING_ADDRESSES",
          "MY_ACCOUNT_INFO",
          "POSTAL_CODE_DETAILS",
        ],
      }),

      getMyAccountInfo: builder.query<AccountInfoState | null, void>({
        query: () => "user/myAccountInfo?dltId=1",
        providesTags: ["MY_ACCOUNT_INFO"],
        transformResponse: (
          response: WindfallRestfulResponse.ResponsePacket<WindfallRestfulResponse.ResponsePieces.AccountInfo>,
        ) => {
          if (response.CallHeader.StatusCode === "SC_OK") {
            return {
              BusinessTypeId: response.Payload.User.BusinessTypeId,
              BusinessTypeName: response.Payload.User.BusinessTypeName,
              BusinessTypeParentId: response.Payload.User.BusinessTypeParentId,
              BusinessTypeParentName: response.Payload.User.BusinessTypeParentName,
              BusinessOtherText: response.Payload.User.BusinessOtherText,
              IsProfessionalOffice: response.Payload.User.IsProfessionalOffice,
              ProfessionVerifier: response.Payload.User.ProfessionVerifier,
              Addresses: response.Payload.User.Addresses,
              PaymentCreditCards: response.Payload.User.PaymentCreditCards
                ? response.Payload.User.PaymentCreditCards
                : [],
              MultilocationSw: response.Payload.MultilocationSw,
              LicenseOptions: response.Payload.LicenseOptions,
              DistributionLists: response.Payload.DistributionLists,
              NamePrefixes: response.Payload.NamePrefixes,
              Regions: response.Payload.Regions,
            } as AccountInfoState;
          } else return null;
        },
      }),

      getLastPurchasedProducts: builder.query<string[], void>({
        query: () => "neo/search/getLastPurchasedProducts",
        providesTags: ["LAST_PURCHASED_PRODUCTS"],
        transformResponse: (response: number[]) => {
          if (!response) return [];
          return response.map(String);
        },
      }),

      // Region list from the users service. Shape (RegionVO): { regionList: [{ regionCd, name }] }.
      // We flatten + remap to the AddressForm/license picker shape ({ Code, Name }).
      getUsStates: builder.query<Array<{ Code: string; Name: string }>, void>({
        query: () => "next/users/us-states",
        transformResponse: (response: any): Array<{ Code: string; Name: string }> => {
          const list: any[] = Array.isArray(response?.regionList) ? response.regionList : [];
          return list.map((r) => ({ Code: r.regionCd ?? "", Name: r.name ?? "" }));
        },
      }),

      // Resolves the license-agency master id for a (license type, region) pair. 404 from
      // the backend becomes a rejected query so callers can treat "no agency in region"
      // as a soft miss via try/catch.
      lookupLicenseAgency: builder.query<{ lamId: number } | null, { lictId: string | number; regionCd: string }>({
        query: ({ lictId, regionCd }) => ({
          url: `next/users/license-agency-by-region?lictId=${encodeURIComponent(String(lictId))}&regionCd=${encodeURIComponent(regionCd)}`,
        }),
      }),

      // Reads the logged-in shopper's licenses from the users service (not windfall's
      // session-cached /user blob). Mirrors the credit-cards migration pattern.
      getMyLicenses: builder.query<Account.Licenses.List, void>({
        query: () => "next/users/my-licenses",
        providesTags: ["MY_LICENSES"],
        transformResponse: (response: unknown): Account.Licenses.List => {
          if (!response || !Array.isArray(response)) return [];
          return response.map((r: any) => ({
            Id: r.slicId != null ? String(r.slicId) : "",
            Number: r.licenseNumber ?? "",
            LicenseAgency: {
              Id: r.lamId != null ? String(r.lamId) : "",
              RegionCd: r.slicRegionCd ?? "",
            },
            LicenseType: {
              Id: r.lictId != null ? String(r.lictId) : "",
              Name: r.licenseTypeName ?? "",
            },
            CountryCD: r.countryCd ?? "",
            NeedState: true,
            ExpirationDate: r.expirationTime ?? null,
          }));
        },
      }),

      licenseUpdate: builder.mutation<void, Account.Licenses.List>({
        query: (licenses) => ({
          url: "next/users/modifyLicense",
          cache: "no-cache",
          method: "POST",
          body: JSON.stringify(
            licenses.map((l) => ({
              id: l.Id && l.Id !== "" ? Number(l.Id) : null,
              lamId: l.LicenseAgency?.Id && l.LicenseAgency.Id !== "" ? Number(l.LicenseAgency.Id) : null,
              licenseNumber: l.Number,
              regionCd: l.LicenseAgency?.RegionCd || null,
              countryCd: l.CountryCD || null,
              expirationTime: l.ExpirationDate || null,
            })),
          ),
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }),
        invalidatesTags: ["USER", "MY_ACCOUNT_INFO", "MY_LICENSES"],
      }),

      deleteLicense: builder.mutation<void, string>({
        query: (licenseId) => ({
          url: "next/users/deleteLicense",
          cache: "no-cache",
          method: "POST",
          body: "id=" + licenseId,
          headers: {
            Accept: "application/json",
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }),
        invalidatesTags: ["USER", "MY_ACCOUNT_INFO", "MY_LICENSES"],
      }),

      // ---- Profile / CC email endpoints (users microservice) -----------------
      // Routed to the users microservice via the `/rest/users/*` Next.js rewrite.
      // queryFn returns a string status code so existing switch-case call sites
      // keep working without rewriting their error handling.

      deleteCCEmail: builder.mutation<string, DeleteCCEmailRequest>({
        queryFn: async (request, _api, _extra, baseQuery) => {
          const result = await baseQuery({
            url: "next/users/delete-cc-email",
            method: "POST",
            cache: "no-cache",
            body: request,
          });
          if (result.error) return { data: mapUsersServiceError(result.error) };
          return { data: "SC_OK" };
        },
        invalidatesTags: ["USER", "MY_ACCOUNT_INFO"],
      }),

      modifyName: builder.mutation<string, ModifyNameRequest>({
        queryFn: async (request, _api, _extra, baseQuery) => {
          const result = await baseQuery({
            url: "next/users/modify-name",
            method: "POST",
            cache: "no-cache",
            body: request,
          });
          if (result.error) return { data: mapUsersServiceError(result.error) };
          return { data: "SC_OK" };
        },
        invalidatesTags: ["USER", "USER_FULL_NAME"],
      }),

      changePassword: builder.mutation<string, ModifyPasswordRequest>({
        queryFn: async (request, _api, _extra, baseQuery) => {
          const result = await baseQuery({
            url: "next/users/modify-password",
            method: "POST",
            cache: "no-cache",
            body: request,
          });
          if (result.error) return { data: mapUsersServiceError(result.error) };
          return { data: "SC_OK" };
        },
        invalidatesTags: ["USER"],
      }),

      verifyEmailAddress: builder.mutation<string, string>({
        queryFn: async (newEmail, _api, _extra, baseQuery) => {
          const result = await baseQuery({
            url: `next/users/verify-email-address/${encodeURIComponent(newEmail.trim())}`,
            cache: "no-cache",
          });
          if (result.error) return { data: mapUsersServiceError(result.error) };
          const data = result.data as { available: boolean } | undefined;
          // Legacy semantics: "SC_OK" = email is registered (found), "SC_ERROR_EMAIL_ADDRESS_NOT_FOUND" = available.
          return { data: data?.available ? "SC_ERROR_EMAIL_ADDRESS_NOT_FOUND" : "SC_OK" };
        },
      }),

      modifyEmail: builder.mutation<string, ModifyEmailRequest>({
        queryFn: async (request, _api, _extra, baseQuery) => {
          const result = await baseQuery({
            url: "next/users/modify-email",
            method: "POST",
            cache: "no-cache",
            body: request,
          });
          if (result.error) return { data: mapUsersServiceError(result.error) };
          return { data: "SC_OK" };
        },
        invalidatesTags: ["USER"],
      }),

      updateUserProfile: builder.mutation<string, string>({
        // Migrated from windfall /user/updateUserProfile → users µ. Callers pass a JSON-stringified
        // object that the microservice deserializes via @JsonAlias-aware UpdateUserProfileRequest,
        // so PascalCase field names (BusinessTypeId / BusinessOtherText / OrderBehalfOfBusiness)
        // still work without any UI shape change.
        queryFn: async (newUserJson, _api, _extra, baseQuery) => {
          let parsed: unknown;
          try {
            parsed = JSON.parse(newUserJson);
          } catch {
            return { data: "SC_ERROR_INCOMPLETE_INFORMATION" };
          }
          const result = await baseQuery({
            url: "next/users/update-user-profile",
            method: "POST",
            cache: "no-cache",
            body: parsed,
          });
          if (result.error) return { data: mapUsersServiceError(result.error) };
          return { data: "SC_OK" };
        },
        invalidatesTags: ["USER", "MY_ACCOUNT_INFO", "ACCOUNT_BUSINESS_TYPE"],
      }),

      createNewCCEmail: builder.mutation<string, CreateCCEmailRequest>({
        queryFn: async (request, _api, _extra, baseQuery) => {
          const result = await baseQuery({
            url: "next/users/create-cc-email",
            method: "POST",
            cache: "no-cache",
            body: request,
          });
          if (result.error) return { data: mapUsersServiceError(result.error) };
          return { data: "SC_OK" };
        },
        invalidatesTags: ["USER", "MY_ACCOUNT_INFO"],
      }),

      /**
       * Admin-only — flips `professional_office_sw` + `profession_verifier` on the impersonated
       * user. Migrated from windfall POST /user/setProfessionalAndVerified → users µ. The µ
       * itself enforces the impersonator gate (returns 403 if the caller's JWT carries no
       * impersonator id), so the UI just forwards the request.
       *
       * Invalidates {@code MY_ACCOUNT_INFO} so {@code useGetAccountSummaryQuery} (which surfaces
       * `isProfessionalOffice` and `professionVerifier`) refetches and the verification screen
       * reflects the new flags.
       */
      setProfessionalAndVerified: builder.mutation<string, SetProfessionalAndVerifiedRequest>({
        queryFn: async (request, _api, _extra, baseQuery) => {
          const result = await baseQuery({
            url: "next/users/set-professional-and-verified",
            method: "POST",
            cache: "no-cache",
            body: request,
          });
          if (result.error) return { data: mapUsersServiceError(result.error) };
          return { data: "SC_OK" };
        },
        // `USER` is invalidated because getUser exposes `IsProfessionalOffice` (line 176),
        // which downstream consumers like convertUserStateToUserDataForAnalytics read to
        // compute `isDentalCustomer` for GA4 / GTM / Klaviyo events. Without this, the cached
        // user keeps the pre-flip value and analytics events misreport segmentation.
        // `MY_ACCOUNT_INFO` is also invalidated so useGetAccountSummaryQuery refetches and
        // the verification screen reflects the new state.
        invalidatesTags: ["USER", "MY_ACCOUNT_INFO"],
      }),

      modifyCCEmail: builder.mutation<string, ModifyCCEmailRequest>({
        queryFn: async (request, _api, _extra, baseQuery) => {
          const result = await baseQuery({
            url: "next/users/modify-cc-email",
            method: "POST",
            cache: "no-cache",
            body: request,
          });
          if (result.error) return { data: mapUsersServiceError(result.error) };
          return { data: "SC_OK" };
        },
        invalidatesTags: ["USER", "MY_ACCOUNT_INFO"],
      }),

      getAccountBusinessType: builder.query<string[], void>({
        query: () => "neo/user/account-business-type",
        providesTags: () => ["ACCOUNT_BUSINESS_TYPE"],
      }),

      addCreditCard: builder.mutation<Account.Billing.CreditCard[], Account.Billing.NewCreditCard>({
        query: (newCreditCard) => ({
          url: "next/orders/user/credit-cards",
          method: "POST",
          body: newCreditCardToOrdersPayload(newCreditCard),
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            _ga: Cookies.get("_ga"),
            gtm_preview_header: Cookies.get("gtm_preview_header"),
          },
        }),
        transformResponse: (response: any): Account.Billing.CreditCard[] =>
          Array.isArray(response) ? response.map(mapOrdersCreditCardToFrontend) : [],
        invalidatesTags: ["MY_ACCOUNT_INFO", "CREDIT_CARDS"],
      }),

      removePaymentMethod: builder.mutation<void, string>({
        query: (cardId) => ({
          url: "next/orders/user/credit-cards/delete",
          method: "POST",
          body: { cardId },
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }),
        invalidatesTags: ["MY_ACCOUNT_INFO", "CREDIT_CARDS"],
      }),

      setPaymentMethodAsPrimary: builder.mutation<void, string>({
        query: (cardId) => ({
          url: "next/orders/user/credit-cards/primary",
          method: "POST",
          body: { cardId },
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }),
        invalidatesTags: ["MY_ACCOUNT_INFO", "CREDIT_CARDS"],
      }),

      getPostalCodeDetails: builder.query<ShippingPostalCdDetails, string>({
        query: (zipCode) => `neo/shipping/${zipCode}/get-postal-code-details`,
        providesTags: ["POSTAL_CODE_DETAILS"],
      }),
      deleteShippingAddress: builder.mutation<void, number>({
        query: (addressId) => ({
          url: "next/users/deleteShippingAddress",
          method: "POST",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: "id=" + addressId,
        }),
        invalidatesTags: ["SHIPPING_ADDRESSES", "PRIMARY_SHIPPING_ADDRESS"],
      }),
      validateAddress: builder.query<Array<Account.Addresses.Address>, AddressValidationGetData>({
        query: (addressDetails) => ({
          url:
            "neo/shipping/validate" +
            "?addressLine1=" +
            encodeURIComponent(addressDetails.street1.substring(0, FieldMaxLengths.ADDRESS_LENGTH)) +
            "&addressLine2=" +
            encodeURIComponent(addressDetails.street2) +
            "&city=" +
            encodeURIComponent(addressDetails.city.substring(0, FieldMaxLengths.ADDRESS_LENGTH)) +
            "&regionCode=" +
            encodeURIComponent(addressDetails.regionCode) +
            "&postalCode=" +
            encodeURIComponent(addressDetails.postalCode) +
            "&countryCode=" +
            encodeURIComponent("US"),
          method: "POST",
          cache: "no-cache",
        }),
      }),
      updateAddress: builder.mutation<Account.Addresses.Address, Account.Addresses.Address>({
        query: (editedShippingAddress) => ({
          url: "next/users/editShippingAddress",
          method: "POST",
          cache: "no-cache",
          body: editedShippingAddress,
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }),
        invalidatesTags: ["SHIPPING_ADDRESSES", "PRIMARY_SHIPPING_ADDRESS"],
      }),
      addAddress: builder.mutation<Account.Addresses.Address, Account.Addresses.NewAddress>({
        query: (newShippingAddress) => ({
          url: "next/users/addShippingAddress",
          method: "POST",
          cache: "no-cache",
          body: newShippingAddress,
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            _ga: Cookies.get("_ga"),
            gtm_preview_header: Cookies.get("gtm_preview_header"),
          },
        }),
        invalidatesTags: ["SHIPPING_ADDRESSES", "PRIMARY_SHIPPING_ADDRESS"],
      }),
      sendEmailCCInvitation: builder.mutation<string, SendCCEmailInvitesRequest>({
        queryFn: async (request, _api, _extra, baseQuery) => {
          const result = await baseQuery({
            url: "next/users/send-cc-email-invites",
            method: "POST",
            cache: "no-cache",
            body: request,
          });
          if (result.error) return { data: mapUsersServiceError(result.error) };
          return { data: "SC_OK" };
        },
        invalidatesTags: [],
      }),

      modifyNickNameAndProfession: builder.mutation<
        string,
        { nickName: string; professionId: number; professionOther: string | null }
      >({
        queryFn: async ({ nickName, professionId, professionOther }, _api, _extra, baseQuery) => {
          const result = await baseQuery({
            url: "next/users/modify-nickname-and-profession/v2",
            method: "POST",
            cache: "no-cache",
            body: {
              nickName,
              pfId: professionId,
              professionOther,
            },
          });
          if (result.error) return { data: mapUsersServiceError(result.error) };
          return { data: "SC_OK" };
        },
        invalidatesTags: ["USER"],
      }),

      createNewPassword: builder.mutation<CreateNewPasswordResponse, CreateNewPasswordRequest>({
        query: (formData) => ({
          url: "users/password-reset",
          cache: "no-cache",
          method: "POST",
          body: formData,
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }),
        invalidatesTags: ["USER", "SHOPPING_CART"],
      }),

      // ---- Replacements for the legacy /user/myAccountInfo fan-out ------------
      // getAccountSummary covers user-scoped fields (distribution lists, business
      // type, profession flags). getNamePrefixes covers the static lookup. Tagged
      // separately so the lookup isn't busted by user mutations.

      getAccountSummary: builder.query<AccountSummaryState, void>({
        // Consumes the microservice payload directly — no PascalCase adapter. If we add a field
        // server-side, it shows up here automatically.
        query: () => "next/users/account-summary",
        providesTags: ["MY_ACCOUNT_INFO"],
      }),

      getNamePrefixes: builder.query<string[], void>({
        query: () => "next/users/name-prefixes",
        // Static lookup — keep it out of MY_ACCOUNT_INFO invalidation so a name change
        // doesn't force a refetch of the dropdown contents.
      }),

      getBusinessTypes: builder.query<BusinessTypeTreeNode[], void>({
        query: () => "next/users/business-types",
        // Server returns PascalCase {BusinessTypeId, BusinessTypeName, BusinessTypeOptions}
        // matching the existing UI BusinessType type — no transformResponse needed.
      }),

      getPasswordBadLexemes: builder.query<string[], void>({
        query: () => "next/users/password-bad-lexemes",
      }),
    }),
  });

/** Tree node shape returned by /users/business-types (matches windfall's WFVBusinessType). */
export type BusinessTypeTreeNode = {
  BusinessTypeId: number;
  BusinessTypeName: string;
  BusinessTypeOptions?: BusinessTypeTreeNode[];
};

export const {
  useGetUserQuery,
  useLazyGetUserQuery,
  useGetTotalOrdersQuery,
  useLoginMutation,
  useSetShippingAddressAsPrimaryMutation,
  useGetPrimaryShippingAddressQuery,
  useLazyGetPrimaryShippingAddressQuery,
  useGetUserNameDetailsQuery,
  useLazyGetUserNameDetailsQuery,
  useGetAllShippingAddressesQuery,
  useLogoutMutation,
  /** @deprecated Use {@link useGetAccountSummaryQuery} (user data) and {@link useGetNamePrefixesQuery} (static lookup). */
  useGetMyAccountInfoQuery,
  useGetAccountSummaryQuery,
  useGetNamePrefixesQuery,
  useGetBusinessTypesQuery,
  useGetPasswordBadLexemesQuery,
  useGetExistingCreditCardsQuery,
  useGetLastPurchasedProductsQuery,
  useGetMyLicensesQuery,
  useGetUsStatesQuery,
  useLazyLookupLicenseAgencyQuery,
  useLicenseUpdateMutation,
  useDeleteLicenseMutation,
  useDeleteCCEmailMutation,
  useModifyNameMutation,
  useChangePasswordMutation,
  useVerifyEmailAddressMutation,
  useModifyEmailMutation,
  useUpdateUserProfileMutation,
  useSetProfessionalAndVerifiedMutation,
  useCreateNewCCEmailMutation,
  useModifyCCEmailMutation,
  useGetAccountBusinessTypeQuery,

  useAddCreditCardMutation,
  useRemovePaymentMethodMutation,
  useSetPaymentMethodAsPrimaryMutation,

  useGetPostalCodeDetailsQuery,
  useDeleteShippingAddressMutation,
  useLazyValidateAddressQuery,
  useUpdateAddressMutation,
  useAddAddressMutation,
  useSendEmailCCInvitationMutation,
  useModifyNickNameAndProfessionMutation,
  useCreateNewPasswordMutation,
  util: { getRunningQueriesThunk },
} = userApi;

export const {
  getUser,
  getTotalOrders,
  login,
  setShippingAddressAsPrimary,
  getPrimaryShippingAddress,
  getAllShippingAddresses,
  getMyAccountInfo,
  getExistingCreditCards,
  getLastPurchasedProducts,
  getMyLicenses,
  getUsStates,
  lookupLicenseAgency,
  licenseUpdate,
  deleteLicense,
  deleteCCEmail,
  modifyName,
  changePassword,
  verifyEmailAddress,
  modifyEmail,
  updateUserProfile,
  createNewCCEmail,
  modifyCCEmail,
  getAccountBusinessType,

  addCreditCard,
  removePaymentMethod,
  setPaymentMethodAsPrimary,

  getPostalCodeDetails,
  deleteShippingAddress,
  validateAddress,
  updateAddress,
  addAddress,
  sendEmailCCInvitation,
  modifyNickNameAndProfession,
  createNewPassword,
} = userApi.endpoints;

export type UserState = {
  // userHasSubscribedToEmail: boolean
  userHasBeenRetrieved: boolean;
  // myAccountHasBeenRetrieved: boolean
  isAuthorized: boolean;
  // backendError: boolean
  // errorMessage: string
  // IsGuestUser: boolean
  UserId?: number;
  IsProfessionalOffice?: boolean;
} & (GuestUserState | LoggedInUserState);
type GuestUserState = {
  IsGuestUser: true;
};
type LoggedInUserState = WindfallRestfulResponse.ResponsePieces.CurrentUser;

/** Raw shape returned by the users microservice's /users/details endpoint (camelCase). */
type UsersDetailsResponse = {
  isAdmin?: boolean;
  ugrId?: number;
  isContractorAdmin?: boolean;
  isVendorAdmin?: boolean;
  userName?: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  emailAddress?: string;
  isAuthenticated?: boolean;
  vendorName?: string;
  loginLink?: string;
  impersonator?: number;
  vendorId?: number;
  prefix?: string;
  nickName?: string;
  pfId?: number;
  professionName?: string;
  professionOther?: string;
  isProfessionalOffice?: boolean;
  activeCd?: number;
  userIdHash?: string;
  guestOnlySw?: boolean;
};

/**
 * Slimmed account-summary shape — what the My Account pages actually consume. Mirrors the
 * microservice JSON exactly (camelCase) so we never need a transformResponse adapter.
 */
export type AccountSummaryState = {
  businessTypeId?: number;
  businessTypeParentId?: number;
  businessOtherText?: string;
  isProfessionalOffice?: boolean;
  professionVerifier?: number;
  multilocationSw?: boolean;
  distributionLists: AccountSummaryDistributionList[];
};

export type AccountSummaryDistributionList = {
  dltId: number;
  emailList: AccountSummaryDistributionListEmail[];
};

export type AccountSummaryDistributionListEmail = {
  dleId: number;
  emailAddress: string;
};

export type AccountInfoState = {
  BusinessTypeId?: number;

  BusinessTypeName?: string;
  BusinessTypeParentId?: number;
  BusinessTypeParentName?: string;
  BusinessOtherText?: string;

  IsProfessionalOffice?: boolean;
  ProfessionVerifier?: number;
  MultilocationSw?: boolean;

  Addresses: Array<Account.Addresses.Address>;
  PaymentCreditCards: Array<Account.Billing.CreditCard>;

  LicenseOptions?: Array<Account.Licenses.LicenseOptions>;

  DistributionLists?: Array<Account.DistributionLists.DistributionList>;

  NamePrefixes?: Array<Account.NamePrefixes>;
  Regions: Array<Account.Addresses.Regions.List>;
};

export type PaymentCardState = Account.Billing.CreditCard & {
  removalStatus?: "processing" | "removed" | "failed";
  removalFailureReason?: string[];
};

export type AddressState = Account.Addresses.Address & {
  removalStatus?: "idle" | "processing" | "removed" | "failed";
  removalFailureReason?: string[];
};

export type AddressValidationGetData = {
  street1: string;
  street2: string;
  city: string;
  regionCode: string;
  postalCode: string;
};

export type UserLoginResponse = {
  userId: number;
  username: string;
  role: "Admin" | "ContractorAdmin" | "VendorAdmin" | "Customer";
  email: string;
  firstName: string;
  lastName: string;
  isProfessionalOffice: boolean;
  mfaEnabled: boolean;
};

export type HelpDeskLoginResponse = UserLoginResponse & {
  n32JWT?: string;
};

export type CreateNewPasswordRequest = {
  token: string;
  password: string;
};

export type CreateNewPasswordResponse = {
  error: CreateNewPasswordError;
};
